import { HeadingText } from '@/components/session/note-text/HeadingText';
import { splitHeadings } from '@/components/session/note-text/noteHeadings';
import { useEffect, useMemo, useRef, useState } from 'react';
import { diffSegmentVariants, noteDiffClasses } from './noteInstructions.styles';
import type { NoteDiffViewProps } from './noteInstructions.types';
import { diffWords } from './wordDiff';

const SETTLE_MS = 1500;

/**
 * Shows the just-applied note change as an inline phrase diff, scrolled into view, then
 * settles to the new text with added phrases still marked. Clicking returns to the normal view.
 */
export const NoteDiffView = ({ change, onDismiss }: NoteDiffViewProps) => {
  const [settled, setSettled] = useState(false);
  const firstChangeRef = useRef<HTMLSpanElement>(null);
  const segments = useMemo(() => diffWords(change.prev, change.next), [change.prev, change.next]);
  const firstChangeIndex = segments.findIndex((segment) => segment.kind !== 'equal');
  // Settled, the deletions are gone; headings are found on the text actually shown.
  const shown = segments.flatMap((segment, index) => (settled && segment.kind === 'del' ? [] : [{ segment, index }]));
  const pieces = splitHeadings(shown.map(({ segment }) => segment.text));

  // Parent keys this component by change.id, so each change remounts, re-scrolls and re-animates.
  useEffect(() => {
    firstChangeRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    const timer = setTimeout(() => setSettled(true), SETTLE_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <button
      type="button"
      data-testid="note-diff-view"
      data-settled={settled}
      aria-label="Breytt nóta — smelltu til að breyta"
      className={noteDiffClasses}
      onClick={onDismiss}
    >
      {shown.map(({ segment, index }, position) => (
        <span
          key={index}
          ref={index === firstChangeIndex ? firstChangeRef : undefined}
          data-diff={segment.kind}
          className={diffSegmentVariants({ kind: settled && segment.kind === 'add' ? 'settledAdd' : segment.kind })}
        >
          <HeadingText pieces={pieces[position]} />
        </span>
      ))}
    </button>
  );
};
